import React from 'react';
import type { EventQandaFragment } from '@graphql/fragments/event.qanda.graphql.generated';
import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { PrettyLink } from '../../components/PrettyLink';
import { useFetchFaqListData } from './useFetchFaqListData';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToParagraphOverride, hed2ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { pxToRem } from '@withjoy/joykit/theme';

export interface FaqListProps
  extends Readonly<{
      // Add props here.
      stackByDefault?: boolean;
      nested?: boolean;
      eventHandle: string;
      tidbits?: ReadonlyArray<EventQandaFragment>;
    }>,
    BoxProps {}

const FaqList = React.forwardRef<HTMLDivElement, FaqListProps>(({ children, nested, stackByDefault, ...restProps }, ref) => {
  const { eventHandle, tidbits } = restProps;
  // skip when rendered nested inside CoupleStory (tidbits are passed via props; no fetch needed)
  const { data, loading } = useFetchFaqListData(eventHandle, !!nested);

  if (nested) {
    // this means that FaqList is being rendered as a nested component w/in either Tidbits or CoupleStory, so should use the tidbits (qanda) passed down
    if (!tidbits || tidbits.length === 0) {
      return null;
    }
    return (
      <Box
        ref={ref}
        {...restProps}
      >
        <SpacingStack
          flexDirection="column"
          spacing={{ _: 8, sm2: 9 }}
        >
          {tidbits.map(({ id, question, answer }) => {
            if (!answer) {
              return null;
            }
            return (
              <Flex
                key={id}
                data-testid={`qanda-${id}`}
                flexDirection="column"
              >
                <GuestSiteTypographyOverride override={hed2ToSubHeadingOverride}>
                  <TextV2
                    wordBreak="break-word"
                    typographyVariant="hed2"
                  >
                    {question}
                  </TextV2>
                </GuestSiteTypographyOverride>
                <GuestSiteTypographyOverride override={body4ToParagraphOverride}>
                  <TextV2
                    wordBreak="break-word"
                    typographyVariant="body4"
                    marginTop={{ _: 3, sm2: pxToRem(10) }}
                    tagName="div"
                  >
                    <PrettyLink>{answer}</PrettyLink>
                  </TextV2>
                </GuestSiteTypographyOverride>
              </Flex>
            );
          })}
        </SpacingStack>
      </Box>
    );
  } else {
    // otherwise, we are rendering the standalone Faqlist page, and should use the data from faq query
    if (loading) return <div style={{ height: '300px' }}></div>; // height set to prevent divs from jumping around while page loads

    const items = data?.eventByName?.info.faq || [];
    return (
      <Box
        ref={ref}
        {...restProps}
      >
        <SpacingStack
          flexDirection="column"
          spacing={{ _: 8, sm2: 9 }}
        >
          {items.map(({ id, question, answer }) => {
            if (!answer) {
              return null;
            }
            return (
              <Flex
                key={id}
                data-testid={`qanda-${id}`}
                flexDirection="column"
              >
                <GuestSiteTypographyOverride override={hed2ToSubHeadingOverride}>
                  <TextV2 typographyVariant="hed2">{question}</TextV2>
                </GuestSiteTypographyOverride>
                <GuestSiteTypographyOverride override={body4ToParagraphOverride}>
                  <TextV2
                    wordBreak="break-word"
                    typographyVariant="body4"
                    marginTop={{ _: 3, sm2: pxToRem(10) }}
                    tagName="div"
                  >
                    <PrettyLink>{answer}</PrettyLink>
                  </TextV2>
                </GuestSiteTypographyOverride>
              </Flex>
            );
          })}
        </SpacingStack>
      </Box>
    );
  }
});

FaqList.displayName = 'FaqList';

export { FaqList };
